import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";

export type Redemption = {
  id: string;
  user_id: string;
  status: "active" | "collected";
  pickup_code: string;
  activated_at: string;
  collected_at: string | null;
};

function toRedemption(r: any): Redemption {
  return {
    id: r.id,
    user_id: r.userId,
    status: r.status,
    pickup_code: r.pickupCode,
    activated_at: r.activatedAt,
    collected_at: r.collectedAt ?? null,
  };
}

export function useTrikotRedemption() {
  const { user } = useAuth();
  const [redemption, setRedemption] = useState<Redemption | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setRedemption(null); setLoading(false); return; }
    let active = true;
    (async () => {
      try {
        const r = await api.trikot.me();
        if (active) setRedemption(r ? toRedemption(r) : null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user?.id]);

  const activate = async (): Promise<Redemption | null> => {
    if (!user) return null;
    const r = await api.trikot.redeem();
    const out = r ? toRedemption(r) : null;
    setRedemption(out);
    return out;
  };

  // Self-collect: marks the user's own redemption as collected and
  // updates local state in place — no page reload needed. Throws on
  // error so the caller can decide how to surface it (toast + UI reset).
  const markCollected = async (): Promise<Redemption | null> => {
    if (!user) return null;
    const r = await api.trikot.collectMine();
    const out = r ? toRedemption(r) : null;
    setRedemption(out);
    return out;
  };

  return { redemption, loading, activate, markCollected };
}
